import { useParams, useSearch, useNavigate } from '@tanstack/react-router'
import { useState, useCallback, useMemo } from 'react'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Textarea } from './ui/textarea'
import { Checkbox } from './ui/checkbox'
import { Label } from './ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useAssetTypes } from '../hooks/useQueries'
import { useAuth } from '../hooks/useAuth'
import { setAuthToken } from '../api/client'
import { AppIcon } from './ui/icon'
import { mdiDownload, mdiLoading, mdiAlert, mdiCodeJson, mdiFileDelimited, mdiZipBox, mdiCheckCircle } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'

type ExportFormat = 'csv' | 'json' | 'csv_zip'

const validFormats: ExportFormat[] = ['csv', 'json', 'csv_zip']

export function AssetsExportPage() {
  const { t } = useTranslation('assets')
  const { org } = useParams({ strict: false }) as { org: string }
  const search = useSearch({ strict: false }) as { format?: string; entities?: string }
  const navigate = useNavigate()
  const { accessToken } = useAuth()
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const formatOptions = useMemo(() => [
    {
      value: 'csv' as ExportFormat,
      label: 'CSV',
      icon: mdiFileDelimited,
      description: t('export.format_csv_description'),
    },
    {
      value: 'json' as ExportFormat,
      label: 'JSON',
      icon: mdiCodeJson,
      description: t('export.format_json_description'),
    },
    {
      value: 'csv_zip' as ExportFormat,
      label: 'CSV ZIP',
      icon: mdiZipBox,
      description: t('export.format_csv_zip_description'),
    },
  ], [t])

  const allEntityKeys = ['assets', 'contracts', 'users', 'documents']

  const format: ExportFormat = validFormats.includes(search.format as ExportFormat)
    ? (search.format as ExportFormat)
    : 'csv'

  const setFormat = (newFormat: ExportFormat) => {
    if (newFormat === 'csv') {
      // CSV only supports one entity — keep the first selected one
      const firstSelected = allEntityKeys.find(k => selectedEntities[k]) ?? allEntityKeys[0]
      const singleEntity: Record<string, boolean> = {}
      for (const key of allEntityKeys) {
        singleEntity[key] = key === firstSelected
      }
      const entities = Object.entries(singleEntity).filter(([, v]) => v).map(([k]) => k)
      navigate({
        search: { ...search, format: newFormat, entities: entities.join(',') } as any,
        replace: true,
      })
    } else {
      navigate({ search: { ...search, format: newFormat } as any, replace: true })
    }
  }

  const selectedEntities: Record<string, boolean> = (() => {
    if (search.entities) {
      const fromUrl = search.entities.split(',').filter(e => allEntityKeys.includes(e))
      const result: Record<string, boolean> = {}
      for (const key of allEntityKeys) {
        result[key] = fromUrl.includes(key)
      }
      return result
    }
    return { assets: true, contracts: false, users: false, documents: false }
  })()

  const navigateEntities = (entities: Record<string, boolean>) => {
    const selected = Object.entries(entities).filter(([, v]) => v).map(([k]) => k)
    navigate({
      search: {
        ...search,
        entities: selected.length > 0 ? selected.join(',') : undefined,
      } as any,
      replace: true,
    })
  }

  const toggleEntity = (entity: string) => {
    const updated = { ...selectedEntities, [entity]: !selectedEntities[entity] }
    navigateEntities(updated)
  }

  const selectSingleEntity = (entity: string) => {
    const updated: Record<string, boolean> = {}
    for (const key of allEntityKeys) {
      updated[key] = key === entity
    }
    navigateEntities(updated)
  }

  const [exportParams, setExportParams] = useState({
    asset_type_code: undefined as string | undefined,
    asset_property_codes: [] as string[],
    include_parent_chain: true,
    created_after: '' as string,
    updated_after: '' as string,
    has_properties: false,
    exclude_incomplete: false,
    include_deleted: false,
  })

  useBreadcrumbs([
    { title: 'Home', path: `/${org}` },
    { title: 'Data', path: `/${org}/data` },
    { title: 'Export', path: `/${org}/data/export` }
  ])

  const { data: assetTypes = [] } = useAssetTypes(org)

  const handlePropertyCodesChange = (value: string) => {
    const codes = value
      .split(/[,\n]/)
      .map(code => code.trim())
      .filter(code => code.length > 0)
    setExportParams(prev => ({ ...prev, asset_property_codes: codes }))
  }

  const handleExport = useCallback(async () => {
    if (!org) return

    setIsExporting(true)
    setExportError(null)

    const token = accessToken || 'pat_demo'
    setAuthToken(token)

    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T').join('-').slice(0, 19)
      const entities = Object.entries(selectedEntities)
        .filter(([, enabled]) => enabled)
        .map(([entity]) => entity)

      const queryParams = new URLSearchParams()
      queryParams.append('format', format)
      queryParams.append('entities', entities.join(','))

      // Add asset-specific filtering parameters when assets are selected
      if (entities.includes('assets')) {
        if (exportParams.asset_type_code) {
          queryParams.append('asset_type_code', exportParams.asset_type_code)
        }
        if (exportParams.asset_property_codes.length > 0) {
          exportParams.asset_property_codes.forEach(code => {
            if (code.trim()) {
              queryParams.append('asset_property_codes', code.trim())
            }
          })
        }
        if (exportParams.include_parent_chain) {
          queryParams.append('include_parent_chain', 'true')
        }
        if (exportParams.created_after) {
          queryParams.append('created_after', exportParams.created_after)
        }
        if (exportParams.updated_after) {
          queryParams.append('updated_after', exportParams.updated_after)
        }
        if (exportParams.has_properties) {
          queryParams.append('has_properties', 'true')
        }
        if (exportParams.exclude_incomplete) {
          queryParams.append('exclude_incomplete', 'true')
        }
        if (exportParams.include_deleted) {
          queryParams.append('include_deleted', 'true')
        }
      }

      const apiUrl = `/api/v1/organizations/${org}/data/export?${queryParams.toString()}`

      const contentTypes: Record<ExportFormat, string> = {
        json: 'application/json',
        csv: 'text/csv;charset=utf-8',
        csv_zip: 'application/zip',
      }
      const extensions: Record<ExportFormat, string> = {
        json: 'json',
        csv: 'csv',
        csv_zip: 'zip',
      }
      const contentType = contentTypes[format]
      const filename = `export-${timestamp}.${extensions[format]}`

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || `HTTP error! status: ${response.status}`)
      }

      const data = await response.blob()
      const blob = new Blob([data], { type: contentType })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

    } catch (error: any) {
      setExportError(error.message || 'Failed to export assets')
      console.error('Export error:', error)
    } finally {
      setIsExporting(false)
    }
  }, [org, format, exportParams, selectedEntities, accessToken])

  const entityOptions = useMemo(() => [
    { key: 'assets', label: t('export.entity_assets') },
    { key: 'contracts', label: t('export.entity_contracts') },
    { key: 'users', label: t('export.entity_users') },
    { key: 'documents', label: t('export.entity_documents') },
  ] as const, [t])

  const hasSelectedEntities = Object.values(selectedEntities).some(Boolean)

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader icon={SECTION_ICONS.export} title={t('export.title')} />

      {/* Format Selection */}
      <Card>
        <CardHeader>
          <CardTitle>{t('export.format_title')}</CardTitle>
          <CardDescription>{t('export.format_description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {formatOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormat(opt.value)}
                data-testid={`format-${opt.value}`}
                className={`relative flex flex-col items-start gap-2 rounded-lg border-2 p-4 text-left transition-colors
                  ${format === opt.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/40 hover:bg-accent/50'
                  }`}
              >
                {format === opt.value && (
                  <AppIcon path={mdiCheckCircle} size="sm" className="absolute top-3 right-3 text-primary" />
                )}
                <div className="flex items-center gap-2">
                  <AppIcon
                    path={opt.icon}
                    size="lg"
                    tone={format === opt.value ? 'default' : 'muted'}
                    className={format === opt.value ? 'text-primary' : undefined}
                  />
                  <span className="font-semibold">{opt.label}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{opt.description}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Entity Selection */}
      <Card>
        <CardHeader>
          <CardTitle>{t('export.entities_title')}</CardTitle>
          <CardDescription>{t('export.entities_description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {entityOptions.map(({ key, label }) => (
              <div key={key} className="flex items-center space-x-2">
                {format === 'csv' ? (
                  <input
                    type="radio"
                    id={`entity-${key}`}
                    name="entity"
                    checked={selectedEntities[key]}
                    onChange={() => selectSingleEntity(key)}
                    data-testid={`entity-${key}-radio`}
                    className="h-4 w-4 accent-primary cursor-pointer"
                  />
                ) : (
                  <Checkbox
                    id={`entity-${key}`}
                    checked={selectedEntities[key]}
                    onCheckedChange={() => toggleEntity(key)}
                    data-testid={`entity-${key}-checkbox`}
                  />
                )}
                <Label htmlFor={`entity-${key}`} className="text-sm cursor-pointer">{label}</Label>
              </div>
            ))}
          </div>

          {/* Asset Options - shown when assets is selected */}
          {selectedEntities.assets && (
            <div className="border rounded-lg p-4 space-y-4 mt-2">
              <h4 className="text-sm font-medium text-foreground">{t('export.asset_options_title')}</h4>

              {/* Asset Type Selection */}
              <div className="space-y-2">
                <Label htmlFor="asset-type">{t('export.asset_type_label')}</Label>
                <Select
                  value={exportParams.asset_type_code || ""}
                  onValueChange={(value) => setExportParams(prev => ({ ...prev, asset_type_code: value === "all" ? undefined : value }))}
                >
                  <SelectTrigger data-testid="asset-type-select">
                    <SelectValue placeholder={t('export.all_asset_types')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('export.all_asset_types')}</SelectItem>
                    {assetTypes.map((type) => (
                      <SelectItem key={type.uid} value={type.code}>
                        {typeof type.name === 'string' ? type.name : (type.name as any)?.en || type.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Property Codes Filter */}
              <div className="space-y-2">
                <Label htmlFor="property-codes">{t('export.property_codes_label')}</Label>
                <Textarea
                  id="property-codes"
                  placeholder="SURFACE&#10;ADDRESS&#10;AREA.*"
                  value={exportParams.asset_property_codes.join('\n')}
                  onChange={(e) => handlePropertyCodesChange(e.target.value)}
                  rows={3}
                  data-testid="property-codes-input"
                />
                <p className="text-xs text-muted-foreground">
                  {t('export.property_codes_hint')}
                </p>
              </div>

              {/* Date Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="created-after">{t('export.created_after_label')}</Label>
                  <Input
                    id="created-after"
                    type="date"
                    value={exportParams.created_after}
                    onChange={(e) => setExportParams(prev => ({ ...prev, created_after: e.target.value }))}
                    data-testid="created-after-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="updated-after">{t('export.updated_after_label')}</Label>
                  <Input
                    id="updated-after"
                    type="date"
                    value={exportParams.updated_after}
                    onChange={(e) => setExportParams(prev => ({ ...prev, updated_after: e.target.value }))}
                    data-testid="updated-after-input"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-parent-chain"
                    checked={exportParams.include_parent_chain}
                    onCheckedChange={(checked) => setExportParams(prev => ({ ...prev, include_parent_chain: Boolean(checked) }))}
                    data-testid="include-parent-chain-checkbox"
                  />
                  <Label htmlFor="include-parent-chain" className="text-sm">{t('export.include_parent_chain')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="has-properties"
                    checked={exportParams.has_properties}
                    onCheckedChange={(checked) => setExportParams(prev => ({ ...prev, has_properties: Boolean(checked) }))}
                    data-testid="has-properties-checkbox"
                  />
                  <Label htmlFor="has-properties" className="text-sm">{t('export.only_with_properties')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="exclude-incomplete"
                    checked={exportParams.exclude_incomplete}
                    onCheckedChange={(checked) => setExportParams(prev => ({ ...prev, exclude_incomplete: Boolean(checked) }))}
                    data-testid="exclude-incomplete-checkbox"
                  />
                  <Label htmlFor="exclude-incomplete" className="text-sm">{t('export.exclude_incomplete')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-deleted"
                    checked={exportParams.include_deleted}
                    onCheckedChange={(checked) => setExportParams(prev => ({ ...prev, include_deleted: Boolean(checked) }))}
                    data-testid="include-deleted-checkbox"
                  />
                  <Label htmlFor="include-deleted" className="text-sm">{t('export.include_deleted')}</Label>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {exportError && (
        <Card className="border-destructive">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-destructive">
              <AppIcon path={mdiAlert} size="md" />
              <span className="font-medium">{t('export.export_failed')}</span>
              <span>{exportError}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleExport}
          disabled={isExporting || !hasSelectedEntities}
          className="min-w-40"
          data-testid="export-button"
        >
          {isExporting ? (
            <>
              <AppIcon path={mdiLoading} size="md" className="mr-2 motion-safe:animate-spin" />
              {t('export.exporting')}
            </>
          ) : (
            <>
              <AppIcon path={mdiDownload} size="md" className="mr-2" />
              {t('export.export_button', { format: formatOptions.find(f => f.value === format)?.label })}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
